/**
 * Use Article Grid - Full Width or Inset to visually present a curated list of destinations. For example, articles or featured brands.
 *
 * @module views/components/ArticleGrid
 * @memberof -Common
 */
import './ArticleGrid.scss';

import React, { useRef } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import AsyncComponent from '@ulta/core/components/AsyncComponent/AsyncComponent';
import GridContainer from '@ulta/core/components/GridContainer/GridContainer';
import { useIntersectionObserver } from '@ulta/core/hooks/useIntersectionObserver/useIntersectionObserver';
import { spreadProps } from '@ulta/core/utils/dom/dom';
import { handleIntersection } from '@ulta/core/utils/intersectionProcessor/intersectionProcessor';

import RailTitle from '@ulta/components/RailTitle/RailTitle';

import { constants } from '@ulta/utils/constants/constants';

/**
 * Represents a ArticleGrid component
 *
 * @method
 * @param {ArticleGridProps} props - React properties passed from composition
 * @returns ArticleGrid
 */
export const ArticleGrid = function( props ){
  const {
    modules,
    articleTextAlign,
    title,
    inset,
    linkAction,
    root = defaultProps.root,
    rootMargin = defaultProps.rootMargin,
    threshold = defaultProps.threshold
  } = props;
  const titleText = title?.text;
  const cardOrientation = 'vertical';
  const articleGridRef = useRef( null );
  useIntersectionObserver( articleGridRef, {
    root: root,
    rootMargin: rootMargin,
    threshold: threshold
  }, handleIntersection( props ) );

  if( !modules || !articleTextAlign ){
    return null;
  }

  return (
    <div className={
      classNames( 'ArticleGrid', {
        [`ArticleGrid__numArticle--0${modules?.length}`]:modules
      } )
    }
    ref={ articleGridRef }
    >
      { titleText &&
        <GridContainer>
          <div className={
            classNames( 'ArticleGrid__mainContainer', {
              'ArticleGrid__mainContainer--inset': inset
            } )
          }
          >
            <div className='ArticleGrid__headerContainer'>
              <RailTitle
                title={ titleText }
                navLink={ linkAction }
              />
            </div>
          </div>
        </GridContainer>
      }
      <GridContainer>
        <div className={
          classNames( 'ArticleGrid__mainContainer', {
            'ArticleGrid__mainContainer--inset': inset
          } )
        }
        >
          { modules?.map( ( content, index ) => {
            const combinedProps = { ...content, articleTextAlign:articleTextAlign, cardOrientation };
            return (
              <div className={
                classNames( 'ArticleGrid__contentContainer', {
                  [`ArticleGrid__contentContainer--${content.moduleName}`]: content.moduleName
                } )
              }
              key={ index }
              >
                <AsyncComponent
                  key={ `${content.id}:${index}` }
                  { ...spreadProps( { ...combinedProps } ) }
                />
              </div>
            );
          } )
          }
        </div>
      </GridContainer>
    </div>
  );
};

/**
 * Property type definitions
 * @typedef ArticleGridProps
 * @type {object}
 * @property {object} title - Sets the title properties
 * @property {object} linkAction - Sets the linkAction
 * @property {object} modules - Sets the article properties
 * @property {string} articleTextAlign - Sets the article text alignment
 * @property {boolean} inset - Boolean to change the layout to be inset
 */

export const propTypes = {
  title: PropTypes.object,
  linkAction: PropTypes.object,
  modules: PropTypes.array,
  articleTextAlign: PropTypes.string,
  inset: PropTypes.bool
};

/**
* Default values for passed properties
* @type object
*/
export const defaultProps = {
  ...constants.INTERSECTION_OBSERVER_OPTIONS
};

ArticleGrid.defaultProps = defaultProps;
ArticleGrid.propTypes = propTypes;

export default ArticleGrid;
